/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { AlertTriangle, Upload, X } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Button } from "../../../../shared/components/ui/button";

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

interface EditImageAssetModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly titleName: string;
  readonly imageType: string; // "Poster", "Backdrop", "Logo", etc.
  readonly imageUrl: string | null;
  readonly originalFileName?: string; // Original uploaded filename
  readonly persistedWarnings?: string[]; // Validation warnings from previous upload
  readonly onUpload: (file: File, onValidation?: (result: ValidationResult) => void) => Promise<void>;
  readonly onDelete: () => Promise<void>;
}

/**
 * Modal for viewing and managing a single image asset
 * Shows image preview, metadata, and upload/delete actions
 */
export function EditImageAssetModal({
  open,
  onClose,
  titleName,
  imageType,
  imageUrl,
  originalFileName,
  persistedWarnings,
  onUpload,
  onDelete,
}: Readonly<EditImageAssetModalProps>): ReactElement | null {
  const [isClosing, setIsClosing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [imageMetadata, setImageMetadata] = useState<{
    width?: number;
    height?: number;
    size?: number;
    type?: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
      setError(null);
      setValidationWarnings([]);
      // Load image metadata if URL exists
      if (imageUrl) {
        loadImageMetadata(imageUrl);
      }
    }
  }, [open, imageUrl]);

  const loadImageMetadata = (url: string) => {
    // Skip loading metadata for r2:// URLs (not supported by browsers)
    // The API will convert these to presigned URLs when data is refetched
    if (url.startsWith("r2://")) {
      return;
    }

    // Load image dimensions
    const img = new Image();
    img.onload = () => {
      setImageMetadata((prev) => ({
        ...prev,
        width: img.naturalWidth,
        height: img.naturalHeight,
      }));
    };
    img.src = url;

    // Try to fetch file size and type from URL
    fetch(url, { method: "HEAD" })
      .then((response) => {
        const contentLength = response.headers.get("content-length");
        const contentType = response.headers.get("content-type");
        if (contentLength || contentType) {
          setImageMetadata((prev) => ({
            ...prev,
            size: contentLength ? Number.parseInt(contentLength, 10) : undefined,
            type: contentType || undefined,
          }));
        }
      })
      .catch(() => {
        // Silently fail - metadata is optional
      });
  };

  if (!open && !isClosing) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setValidationWarnings([]);
    try {
      await onUpload(file, (validation) => {
        // Handle validation errors (block upload)
        if (!validation.valid && validation.errors.length > 0) {
          setError(validation.errors.join("; "));
          setUploading(false);
          return;
        }

        // Handle validation warnings (allow upload, show warnings)
        if (validation.warnings.length > 0) {
          setValidationWarnings(validation.warnings);
          // Auto-dismiss warnings after 10 seconds
          setTimeout(() => {
            setValidationWarnings([]);
          }, 10000);
        }
      });
      // Don't auto-close modal - let user see validation results and the updated image
    } catch (err) {
      console.error("Failed to upload image:", err);
      setError(err instanceof Error ? err.message : "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!globalThis.confirm(`Delete ${imageType.toLowerCase()}?`)) return;

    setDeleting(true);
    setError(null);
    try {
      await onDelete();
      handleClose();
    } catch (err) {
      console.error("Failed to delete image:", err);
      setError(err instanceof Error ? err.message : "Failed to delete image");
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isVisible = open && !isClosing;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/60 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-900/40 backdrop-blur-md border border-neutral-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div>
            <h2 className="text-xl font-semibold text-white">{imageType}</h2>
            <p className="text-sm text-neutral-400 mt-1">{titleName}</p>
          </div>
          <button type="button" onClick={handleClose} className="text-neutral-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded text-sm text-red-400">{error}</div>
          )}

          {/* Validation Warnings */}
          {validationWarnings.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded">
              <div className="flex items-center gap-2 text-xs font-medium text-yellow-400">
                <AlertTriangle className="h-4 w-4" />
                <span>Image Quality Warnings:</span>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-yellow-300">
                {validationWarnings.map((warning) => (
                  <li key={warning} className="flex items-start gap-2">
                    <span className="text-yellow-500">•</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {imageUrl ? (
            <div className="space-y-4">
              {/* Image Preview */}
              {imageUrl.startsWith("r2://") ? (
                <div className="flex justify-center bg-neutral-950 rounded-lg p-8">
                  <div className="text-center text-neutral-400">
                    <div className="animate-pulse mb-2">Loading image...</div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center bg-neutral-950 rounded-lg p-4">
                  <img src={imageUrl} alt={imageType} className="max-h-96 max-w-full object-contain rounded" />
                </div>
              )}

              {/* Image Metadata */}
              {imageMetadata && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-xs pb-2">
                    {originalFileName && (
                      <div>
                        <span className="text-neutral-500">Name:</span>
                        <span className="ml-2 text-neutral-300">{originalFileName}</span>
                      </div>
                    )}
                    {imageMetadata.width && imageMetadata.height && (
                      <div>
                        <span className="text-neutral-500">Resolution:</span>
                        <span className="ml-2 text-neutral-300">
                          {imageMetadata.width} × {imageMetadata.height}
                        </span>
                      </div>
                    )}
                    {imageMetadata.size && (
                      <div>
                        <span className="text-neutral-500">File Size:</span>
                        <span className="ml-2 text-neutral-300">{formatFileSize(imageMetadata.size)}</span>
                      </div>
                    )}
                    {imageMetadata.type && (
                      <div>
                        <span className="text-neutral-500">Type:</span>
                        <span className="ml-2 text-neutral-300">{imageMetadata.type}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-6 border-t border-neutral-800">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      void handleFileSelect(e);
                    }}
                    disabled={uploading || deleting}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploading || deleting}
                    className="w-full pointer-events-none"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading..." : "Replace Image"}
                  </Button>
                </label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void handleDelete();
                  }}
                  disabled={uploading || deleting}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="text-neutral-500 text-sm">No {imageType.toLowerCase()} uploaded</div>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    void handleFileSelect(e);
                  }}
                  disabled={uploading}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading}
                  className="bg-lumi-accent hover:bg-lumi-accent/50 text-black border-0 pointer-events-none"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "Uploading..." : `Upload ${imageType}`}
                </Button>
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-neutral-800">
          {persistedWarnings && persistedWarnings.length > 0 && (
            <div className="text-xs">
              <div className="flex items-center gap-2 font-medium text-yellow-400">
                <AlertTriangle className="h-4 w-4" />
                <span>Quality Notes:</span>
              </div>
              <ul className="mt-2 space-y-1 text-yellow-300">
                {persistedWarnings.map((warning) => (
                  <li key={warning} className="flex items-start gap-2">
                    <span className="text-yellow-500">•</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button type="button" onClick={handleClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
